/* eslint-disable no-param-reassign */
import * as Sentry from '@sentry/vue'
import ReadinessCheckerApi from '@/api/readinessChecker'
import kvSyncSubscriber from '@/mqtt/kvSync'
import { trackReadinessChecker } from '@/business-logic/ai-tracking'
// import { isFeatureEnabled } from '@/vuex/modules/featureToggles.module'

export const MIN_SLIDES_FOR_CHECK = 5

// AHA-41674: when this flag's variant is 'treatment', free-plan users no longer get the
// readiness check auto-triggered (they trigger it manually). Paid users are unaffected.
// Variant-based gating (not boolean) so Mixpanel can tell "flag off" from "flag failed to load".
export const READINESS_AUTO_PAID_ONLY_FLAG = 'AHA-41674-readiness-auto-paid-only'

const ISSUE_GONE_STATUSES = [404, 410]
const ISSUE_GONE_ERROR_CODES = ['issue_not_found', 'slide_not_found']

class ResolveIssueError extends Error {
  constructor({ status, body }) {
    super(body?.message || body?.error || 'Resolve issue failed')
    this.status = status
    this.body = body
  }
}

const readResolveErrorCode = (error, status) => {
  const body = error?.body ?? error?.data
  const code = body?.errorCode ?? body?.error_code ?? body?.code
    ?? (typeof body?.error === 'string' ? body.error : undefined)
  if (code) return String(code)
  return status ? `http_${status}` : 'network_error'
}

const SET_POTENTIAL_ISSUES = 'SET_POTENTIAL_ISSUES'
const ADD_POTENTIAL_ISSUES = 'ADD_POTENTIAL_ISSUES'
const SET_IS_TRIGGERING = 'SET_IS_TRIGGERING'
const SET_HAS_FETCHED_INITIAL_ISSUES = 'SET_HAS_FETCHED_INITIAL_ISSUES'
const SET_TRIGGER_FAILED = 'SET_TRIGGER_FAILED'
const SET_LAST_CHECKED = 'SET_LAST_CHECKED'
const COUNT_POTENTIAL_ISSUES = 'COUNT_POTENTIAL_ISSUES'
const SET_CURRENT_RUN_ID = 'SET_CURRENT_RUN_ID'
const REMOVE_RESOLVED_ISSUES = 'REMOVE_RESOLVED_ISSUES'
const SET_HAS_AUTO_TRIGGERED = 'SET_HAS_AUTO_TRIGGERED'
const SET_IS_READINESS_CHECK_STALE = 'SET_IS_READINESS_CHECK_STALE'

const _state = {
  potentialIssues: [],
  isTriggering: false,
  hasFetchedInitialIssues: false,
  triggerFailed: false,
  lastChecked: null,
  currentRunId: null,
  hasAutoTriggered: false, // Track if auto-trigger has happened
  isReadinessCheckStale: false,
}

const _getters = {
  potentialIssues(state) {
    return state.potentialIssues
  },

  isTriggering(state) {
    return state.isTriggering
  },

  triggerFailed(state) {
    return state.triggerFailed
  },

  lastChecked(state) {
    return state.lastChecked
  },

  lastCompletedCheckAt: (state, _, __, rootGetters) => {
    return state.lastChecked || rootGetters['presentationSetting/readinessLastCheckedAt'] || null
  },

  hasCompletedCheck: (_, getters) => {
    return !!getters.lastCompletedCheckAt
  },

  isReadinessCheckStale(state) {
    return state.isReadinessCheckStale
  },

  currentRunId(state) {
    return state.currentRunId
  },

  canInvalidateFreshResults: (state, getters) => {
    return getters.hasCompletedCheck && !state.isTriggering && !state.isReadinessCheckStale
  },

  canUseReadinessChecker: (_, __, ___, rootGetters) => {
    return rootGetters['featureToggles/isFeatureEnabled']('release-AHA-40468-readiness-checker')
  },

  hasEnoughSlidesForCheck: (_, __, rootState) => {
    const slideCount = rootState.presentation?.slides?.length || 0
    return slideCount >= MIN_SLIDES_FOR_CHECK
  },

  // AHA-41674: 'treatment' variant restricts auto-trigger to paid users. Any non-treatment
  // value (control / disabled / unloaded) preserves the original auto-for-everyone behaviour.
  isReadinessAutoPaidOnly: (_, __, ___, rootGetters) => {
    return rootGetters['featureToggles/getVariant'](READINESS_AUTO_PAID_ONLY_FLAG)?.name === 'treatment'
  },

  // AHA-41674: the app's canonical free-plan check (mirrors `plan.group === 'free'` used elsewhere).
  isFreePlanForReadiness: (_, __, rootState) => {
    return rootState.plan?.group === 'free'
  },
  canUseActionsForPotentialIssues: (_, __, ___, rootGetters) => {
    return rootGetters['featureToggles/isFeatureEnabled']('release-AHA-40478-actions-for-potential-issues')
  },

  issuesByType: (state) => (type) => {
    return state.potentialIssues.filter((issue) => issue.type === type)
  },

  issuesByScope: (state) => (scope) => {
    return state.potentialIssues.filter((issue) => issue.scope === scope)
  },

  issuesForSlide: (state) => (slideId) => {
    return state.potentialIssues.filter(
      (issue) => issue.scope === 'slide' && issue.slideId === slideId,
    )
  },

  issueCount(state) {
    return state.potentialIssues.length
  },

  // Sort issues by slide order (grouped by slide)
  sortedPotentialIssues: (state, getters, rootState) => {
    const slides = rootState.presentation?.slides || []
    const slideOrderMap = new Map()
    slides.forEach((slide, index) => {
      slideOrderMap.set(slide.id, index)
    })

    return [...state.potentialIssues].sort((a, b) => {
      // Presentation-level issues (no slideId) go first
      const orderA = a.slideId ? (slideOrderMap.get(a.slideId) ?? Infinity) : -1
      const orderB = b.slideId ? (slideOrderMap.get(b.slideId) ?? Infinity) : -1
      return orderA - orderB
    })
  },

  needsAttentionIssues(_, getters) {
    // Critical issues: severity = 3, sorted by slide order
    return getters.sortedPotentialIssues.filter((issue) => issue.severity === 3)
  },

  moreSuggestionsIssues(_, getters) {
    // Optional improvements: severity != 3, sorted by slide order
    return getters.sortedPotentialIssues.filter((issue) => issue.severity !== 3)
  },
}

const mutations = {
  [SET_POTENTIAL_ISSUES](state, issues) {
    state.potentialIssues = issues
    // Update lastChecked when setting issues (e.g., from initial fetch)
    if (issues && issues.length > 0 && issues[0].createdAt) {
      state.lastChecked = issues.map((i) => i?.createdAt).filter(Boolean).sort().pop()
    }
  },
  [COUNT_POTENTIAL_ISSUES](state, issues) {
    state.potentialIssues = issues.length
  },

  [ADD_POTENTIAL_ISSUES](state, issues) {
    if (!issues || issues.length === 0) return

    // Filter out duplicates based on issue ID
    const existingIds = new Set(
      state.potentialIssues
        .map((issue) => issue.id)
        .filter((id) => id !== undefined),
    )
    const uniqueNewIssues = issues.filter(
      (issue) => issue.id === undefined || !existingIds.has(issue.id),
    )

    // Always append new issues (no batch replacement)
    state.potentialIssues = [...state.potentialIssues, ...uniqueNewIssues]
    const newCreatedAt = issues[0]?.createdAt
    // Update lastChecked with latest timestamp
    if (newCreatedAt && (!state.lastChecked || newCreatedAt > state.lastChecked)) {
      state.lastChecked = newCreatedAt
    }
  },

  [SET_IS_TRIGGERING](state, { isTriggering }) {
    state.isTriggering = isTriggering
  },

  [SET_HAS_FETCHED_INITIAL_ISSUES](state, hasFetchedInitialIssues) {
    state.hasFetchedInitialIssues = hasFetchedInitialIssues
  },

  [SET_TRIGGER_FAILED](state, { triggerFailed }) {
    state.triggerFailed = triggerFailed
  },

  [SET_LAST_CHECKED](state, lastChecked) {
    state.lastChecked = lastChecked
  },

  [SET_CURRENT_RUN_ID](state, runId) {
    state.currentRunId = runId
  },

  [REMOVE_RESOLVED_ISSUES](state, resolvedIssueIds) {
    // Remove issues by their IDs (more stable than indices)
    if (!resolvedIssueIds || !Array.isArray(resolvedIssueIds)) return

    const resolvedIdsSet = new Set(resolvedIssueIds)
    state.potentialIssues = state.potentialIssues.filter(
      (issue) => !resolvedIdsSet.has(issue.id),
    )
  },

  [SET_HAS_AUTO_TRIGGERED](state, hasAutoTriggered) {
    state.hasAutoTriggered = hasAutoTriggered
  },

  [SET_IS_READINESS_CHECK_STALE](state, isReadinessCheckStale) {
    state.isReadinessCheckStale = isReadinessCheckStale
  },
}

const getReadinessCheckerTopic = (presentationId) => {
  return `presentation-${presentationId}-readiness-checker`
}

const actions = {
  subscribeReadinessChecker({
    dispatch, commit, state,
  }, { presentationId }) {
    if (!presentationId) return
    const topic = getReadinessCheckerTopic(presentationId)
    kvSyncSubscriber.subscribeKey(topic, (_, message) => {
      try {
        const payload = JSON.parse(message)

        // Handle new issues
        if (payload.issues) {
          dispatch('handleReadinessCheckerResultAction', {
            potentialIssues: payload.issues || [],
          })
        }

        // Handle resolved issues
        if (payload.resolvedIssues && Array.isArray(payload.resolvedIssues)) {
          commit(REMOVE_RESOLVED_ISSUES, payload.resolvedIssues)
        }
        // Handle finished run
        if (payload.finishedRun) {
          // Only stop loading if this is the current run
          if (payload.finishedRun === state.currentRunId) {
            commit(SET_IS_TRIGGERING, { isTriggering: false })
            commit(SET_CURRENT_RUN_ID, null)
            commit(SET_LAST_CHECKED, new Date().toISOString())
            commit(SET_IS_READINESS_CHECK_STALE, false)
          }
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: 'readiness-checker-mqtt' },
        })
      }
    })
  },

  unsubscribeReadinessChecker(_, { presentationId }) {
    if (!presentationId) return
    kvSyncSubscriber.unsubscribeKey(getReadinessCheckerTopic(presentationId))
  },

  async triggerReadinessCheckerAction({ commit, rootState }) {
    try {
      commit(SET_TRIGGER_FAILED, { triggerFailed: false })

      const presentationId = rootState.presentation?.id

      if (!presentationId) {
        return
      }

      trackReadinessChecker({ action: 'trigger', stage: 'started' })

      commit(SET_IS_TRIGGERING, { isTriggering: true })

      const response = await ReadinessCheckerApi.triggerReadinessChecker({ presentationId })
      if (response?.runId) {
        commit(SET_CURRENT_RUN_ID, response.runId)
      }
    } catch (error) {
      commit(SET_IS_TRIGGERING, { isTriggering: false })
      commit(SET_TRIGGER_FAILED, { triggerFailed: true })

      trackReadinessChecker({ action: 'trigger', stage: 'failed', error })

      Sentry.captureException(error, {
        tags: { feature: 'readiness-checker-trigger' },
        extra: { error },
      })
    }
  },

  async getReadinessPotentialIssuesAction({ commit, rootState, getters }) {
    const presentationId = rootState.route.params.id
    if (!presentationId || !getters.hasEnoughSlidesForCheck) {
      return
    }

    try {
      trackReadinessChecker({ action: 'fetchIssues', stage: 'started' })

      const potentialIssues = await ReadinessCheckerApi.getReadinessPotentialIssues({ presentationId })

      if (potentialIssues && potentialIssues.length > 0) {
        commit(SET_POTENTIAL_ISSUES, potentialIssues)
        commit(SET_IS_READINESS_CHECK_STALE, false)
      }
    } catch (error) {
      trackReadinessChecker({ action: 'fetchIssues', stage: 'failed' })

      Sentry.captureException(error, {
        tags: { feature: 'readiness-checker-fetch' },
      })
    } finally {
      commit(SET_HAS_FETCHED_INITIAL_ISSUES, true)
      commit(SET_IS_TRIGGERING, { isTriggering: false })
    }
  },

  // BOO-119: the initial fetch of existing issues is dispatched once in the
  // quality-control components' mounted(), but at mount `presentation.slides` is
  // frequently still loading (length 0). In that case getReadinessPotentialIssuesAction
  // bails BEFORE its try/finally, so `hasFetchedInitialIssues` stays false and the
  // existing backend issues are never loaded — leaving the panel showing a false
  // "All clear". This action is the re-trigger: once enough slides are present and
  // the initial fetch hasn't succeeded yet, fetch existing issues exactly once.
  // Callers wire it to the slide-count watcher; the `hasFetchedInitialIssues` guard
  // (set in getReadinessPotentialIssuesAction's finally) makes it idempotent so it
  // never re-fetches on subsequent slide edits.
  async ensureInitialIssuesFetchedAction({ state, getters, dispatch }) {
    if (state.hasFetchedInitialIssues || !getters.hasEnoughSlidesForCheck) {
      return
    }
    await dispatch('getReadinessPotentialIssuesAction')
  },

  handleReadinessCheckerResultAction({ commit }, { potentialIssues }) {
    if (potentialIssues && potentialIssues.length > 0) {
      commit(ADD_POTENTIAL_ISSUES, potentialIssues)
    } else {
      commit(SET_IS_TRIGGERING, { isTriggering: false })
    }
  },

  resetReadinessCheckerAction({ commit }) {
    commit(SET_POTENTIAL_ISSUES, [])
    commit(SET_IS_TRIGGERING, { isTriggering: false })
    commit(SET_HAS_FETCHED_INITIAL_ISSUES, false)
    commit(SET_TRIGGER_FAILED, { triggerFailed: false })
    commit(SET_LAST_CHECKED, null)
    commit(SET_CURRENT_RUN_ID, null)
    commit(SET_HAS_AUTO_TRIGGERED, false)
    commit(SET_IS_READINESS_CHECK_STALE, false)
  },

  markReadinessCheckAsStaleAction({ commit, getters }) {
    if (!getters.canInvalidateFreshResults) return
    commit(SET_IS_READINESS_CHECK_STALE, true)
  },

  async tryAutoTriggerReadinessCheckerAction({
    state, commit, dispatch, getters, rootGetters,
  }) {
    // Guard 0: AI consent — passive readiness checker disabled
    const isAutoDisabled = rootGetters['featureSettings/isFeatureDisabled']('aiReadinessCheckerAuto')
    if (isAutoDisabled) return

    // Guard 1: Can't use readiness checker by flag
    if (!getters.canUseReadinessChecker) return

    // Guard 2: Already auto-triggered
    if (state.hasAutoTriggered) return

    // Guard 3: Not enough slides
    if (!getters.hasEnoughSlidesForCheck) return

    // Guard 4: Not checked yet and not already checking
    if (getters.hasCompletedCheck || state.isTriggering) {
      return
    }

    // Guard 5 (AHA-41674): free-plan users are excluded from auto-trigger when the flag is on.
    // They can still trigger the check manually via the "Review" button / alert badge.
    if (getters.isReadinessAutoPaidOnly && getters.isFreePlanForReadiness) return

    // Trigger new check
    await dispatch('triggerReadinessCheckerAction')

    // Mark as auto-triggered to prevent duplicate triggers
    commit(SET_HAS_AUTO_TRIGGERED, true)
  },

  async resolveIssuesAction({ commit, rootState }, { issueId, issueType }) {
    const presentationId = rootState.presentation?.id
    if (!presentationId || issueId === undefined) return { success: false, skipped: true }

    trackReadinessChecker({ action: 'resolveIssues', stage: 'started', issueType })

    try {
      const body = await ReadinessCheckerApi.resolveIssues({ presentationId, issueId })
      if (body?.success === false) {
        throw new ResolveIssueError({ status: 200, body })
      }
      commit(REMOVE_RESOLVED_ISSUES, [issueId])

      trackReadinessChecker({ action: 'resolveIssues', stage: 'succeeded', issueType })
      return { success: true }
    } catch (error) {
      const status = error?.status ?? 0
      const errorCode = readResolveErrorCode(error, status)
      const issueGone = ISSUE_GONE_STATUSES.includes(status) || ISSUE_GONE_ERROR_CODES.includes(errorCode)
      if (issueGone) commit(REMOVE_RESOLVED_ISSUES, [issueId])

      trackReadinessChecker({
        action: 'resolveIssues', stage: 'failed', error, status, errorCode, issueType,
      })

      Sentry.captureException(error, {
        tags: { feature: 'readiness-checker-resolve' },
        extra: { status, errorCode, issueType },
      })
      return {
        success: false, issueGone, status, errorCode,
      }
    }
  },

  async ignoreIssueAction({ commit, rootState }, { issueId }) {
    const presentationId = rootState.presentation?.id
    if (!presentationId || issueId === undefined) return

    trackReadinessChecker({ action: 'ignoreIssue', stage: 'started' })

    try {
      await ReadinessCheckerApi.ignoreIssue({ id: issueId, presentationId })
      commit(REMOVE_RESOLVED_ISSUES, [issueId])

      trackReadinessChecker({ action: 'ignoreIssue', stage: 'succeeded' })
    } catch (error) {
      trackReadinessChecker({ action: 'ignoreIssue', stage: 'failed' })

      Sentry.captureException(error, {
        tags: { feature: 'readiness-checker-ignore' },
      })
    }
  },

  async recheckReadinessAction({ commit, dispatch }) {
    // Step 1: Set loading and clear all issues
    commit(SET_IS_TRIGGERING, { isTriggering: true })
    commit(SET_TRIGGER_FAILED, { triggerFailed: false })
    commit(SET_LAST_CHECKED, null)
    commit(SET_CURRENT_RUN_ID, null)

    // Step 2: Trigger readiness checker
    await dispatch('triggerReadinessCheckerAction')
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}
